
import { defineComponent, onMounted, ref, watch, computed } from 'vue';
import PedidoService from '@/services/sit-pedidos/pedido.service';
import { mask } from 'vue-the-mask';
import globalStore from '@/store/modules/global';

export default defineComponent({
  directives: { mask },
  props: {
    formSubstituicao: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    // Watch para logradouroNumero
    // Atualiza logradouroNumero e emite para o pai

    const cpf = ref('');
    const cooperado = ref<any>(null);
    const erro = ref('');

    // Veículo será excluído quando: NÃO é da empresa (permiteAproveitar === false)
    // OU é da empresa mas o cooperado escolheu NÃO reaproveitar ('Não').
    // Só faz sentido quando há placa. Alimenta o aviso vermelho ao lado da placa.
    const veiculoSeraExcluido = computed(() => {
      const p: any = props.formSubstituicao.pedido;
      if (!p || !p.placa) return false;
      if (p.permiteAproveitar === false) return true;
      if (p.permiteAproveitar === true && cooperado.value && cooperado.value.aproveitarVeiculo === 'Não') {
        return true;
      }
      return false;
    });
    const loading = ref(false);
    const rules = {
      required: (v: string) => !!v || 'CPF obrigatório',
      cpf: (v: string) => /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(v) || 'CPF inválido',
    };

    // CEP
    const cep = ref('');
    const endereco = ref<any>(null);
    const enderecoEncontrado = ref(false); // Controla se o endereço foi encontrado automaticamente
    const camposPreenchidosAPI = ref({
      logradouro: false,
      municipio: false,
      bairro: false,
      uf: false,
    });
    const erroCep = ref('');
    const loadingCep = ref(false);
    const loadingSaveCooperado = ref(false);
    const cepRules = {
      required: (v: string) => !!v || 'Campo é obrigatório',
      cep: (v: string) => /^\d{5}-\d{3}$/.test(v) || 'CEP inválido',
    };

    const numeroRules = (v: string) => {
      if (!v) return 'Campo é obrigatório';
      const valor = v.toString().toLowerCase();
      return /^\d+$/.test(valor) || valor === 's/n' || 'Número ou S/N válido';
    };
    const cooperadoData = ref<any>(null);

    const refForm = ref();

    const areaAtuacao = ref<any[]>([]);

    let debounceCpf: any = null;
    function buscarCooperado() {
      listAreasAtuacao();
      erro.value = '';
      cooperado.value = null;
      if (!rules.required(cpf.value) || !rules.cpf(cpf.value)) {
        erro.value = 'Informe um CPF válido.';
        return;
      }
      loading.value = true;
      const cpfLimpo = cpf.value.replace(/\D/g, '');
      PedidoService.searchCooperado(cpfLimpo).subscribe({
        next: (response: any) => {
          if (response?.resultado) {
            globalStore.setSnackBar({
              show: true,
              message: '' + response.mensagem,
              type: 'warning',
              timeout: 4000,
            });
            loading.value = false;
            return;
          }
          cooperado.value = response;
          loading.value = false;
        },
        error: () => {
          erro.value = 'Cooperado não encontrado ou erro na busca.';
          loading.value = false;
        },
      });
    }

    let debounceCep: any = null;
    function buscarEndereco() {
      erroCep.value = '';
      endereco.value = null;
      enderecoEncontrado.value = false;
      if (!cepRules.required(cep.value) || !cepRules.cep(cep.value)) {
        erroCep.value = 'Informe um CEP válido.';
        return;
      }
      loadingCep.value = true;
      const cepLimpo = cep.value.replace(/\D/g, '');
      PedidoService.searchEnderecoCEP(cepLimpo).subscribe({
        next: (response: any) => {
          if (Object.keys(response).length === 0) {
            // CEP não encontrado - criar objeto vazio para permitir edição manual
            endereco.value = {
              logradouro: '',
              municipio: '',
              bairro: '',
              uf: '',
              cep: cep.value,
              logradouroNumero: '',
              logradouroComplemento: '',
            };
            camposPreenchidosAPI.value = {
              logradouro: false,
              municipio: false,
              bairro: false,
              uf: false,
            };
            enderecoEncontrado.value = false; // Permite edição manual
            globalStore.setSnackBar({
              show: true,
              message: 'CEP não encontrado. Preencha os campos manualmente.',
              type: 'warning',
              timeout: 4000,
            });
            loadingCep.value = false;
            return;
          }
          const dadosAPI = response.data || response;
          endereco.value = dadosAPI;

          // Marcar quais campos vieram preenchidos da API
          camposPreenchidosAPI.value = {
            logradouro: isFieldFilled(dadosAPI.logradouro),
            municipio: isFieldFilled(dadosAPI.municipio),
            bairro: isFieldFilled(dadosAPI.bairro),
            uf: isFieldFilled(dadosAPI.uf),
          };

          enderecoEncontrado.value = true; // CEP encontrado automaticamente
          loadingCep.value = false;
        },
        error: () => {
          // Erro na busca - criar objeto vazio para permitir edição manual
          endereco.value = {
            logradouro: '',
            municipio: '',
            bairro: '',
            uf: '',
            cep: cep.value,
            logradouroNumero: '',
            logradouroComplemento: '',
          };
          camposPreenchidosAPI.value = {
            logradouro: false,
            municipio: false,
            bairro: false,
            uf: false,
          };
          enderecoEncontrado.value = false; // Permite edição manual
          erroCep.value = 'Endereço não encontrado. Preencha os campos manualmente.';
          loadingCep.value = false;
        },
      });
    }

    watch(cep, novoCep => {
      if (debounceCep) clearTimeout(debounceCep);
      if (!novoCep || novoCep.length < 9) {
        endereco.value = null;
        enderecoEncontrado.value = false;
        camposPreenchidosAPI.value = {
          logradouro: false,
          municipio: false,
          bairro: false,
          uf: false,
        };
        erroCep.value = '';
        return;
      }
      debounceCep = setTimeout(() => {
        buscarEndereco();
      }, 600);
    });

    watch(cpf, novoCpf => {
      if (debounceCpf) clearTimeout(debounceCpf);
      if (!novoCpf || novoCpf.length < 14) {
        cooperado.value = null;
        erro.value = '';
        return;
      }
      debounceCpf = setTimeout(() => {
        buscarCooperado();
      }, 600);
    });

    function formatDataNascimento(data: string) {
      if (!data) return '';
      // Se vier no formato 'YYYYMMDD'
      if (/^\d{8}$/.test(data)) {
        const ano = data.substring(0, 4);
        const mes = data.substring(4, 6);
        const dia = data.substring(6, 8);
        return `${dia}/${mes}/${ano}`;
      }
      // Se vier em outro formato, tenta converter normalmente
      const date = new Date(data);
      return isNaN(date.getTime()) ? data : date.toLocaleDateString('pt-BR');
    }

    function sendForm() {
      if (refForm.value && !refForm.value.validate()) {
        return;
      }
      loadingSaveCooperado.value = true;
      cooperadoData.value = {
        idTransportadora: props.formSubstituicao.pedido.idTransportadora,
        tipoPedido: props.formSubstituicao.pedido.tipoPedido,
        cooperado: {
          nome: cooperado.value.nome,
          nomeReduzido: cooperado.value.nomeReduzido,
          cpf: removeMascara(cpf.value),
          rg: null,
          cnhNumero: null,
          cnhValidade: null,
          logradouro: endereco.value.logradouro,
          cep: removeMascara(endereco.value.cep || cep.value),
          municipio: endereco.value.municipio,
          bairro: endereco.value.bairro,
          uf: endereco.value.uf,
          dataNascimento: cooperado.value.dataNascimento,
          sexo: null,
          telefone: removeMascara(cooperado.value.telefone),
          logradouroComplemento: endereco.value.logradouroComplemento,
          logradouroNumero: endereco.value.logradouroNumero,
          areaAtuacaoId: cooperado.value.areaAtuacaoId,
          idCooperado: props.formSubstituicao.pedido.idCooperado,
          // Regra: reaproveita ('Sim') -> true. Tem placa e NÃO reaproveita
          // (não é da empresa, ou é mas escolheu 'Não') -> false, pra o veículo
          // ser excluído. Só é null quando NÃO há placa (nenhum veículo).
          aproveitarVeiculo:
            cooperado.value.aproveitarVeiculo === 'Sim'
              ? true
              : props.formSubstituicao.pedido.placa
                ? false
                : null,
          // Documento do proprietário — só quando confirmamos que é da empresa.
          // Vai persistido no back, ao lado do aproveitar_veiculo.
          documentoProprietario:
            props.formSubstituicao.pedido.permiteAproveitar === true
              ? props.formSubstituicao.pedido.documentoProprietario || null
              : null,
        },
      };

      PedidoService.substituirCooperado(cooperadoData.value).subscribe({
        next: (response: any) => {
          emit('success', { nextStep: 3, idPedido: response.idPedido, sexo: cooperado.value.sexo });
          loadingSaveCooperado.value = false;
          refForm.value.reset();
          cpf.value = '';
        },
        error: err => {
          loadingSaveCooperado.value = false;
        },
      });
      // emit('cooperadoData', cooperadoData.value);
    }

    function removeMascara(valor: string): string {
      return valor ? valor.replace(/\D/g, '') : '';
    }

    function isFieldFilled(value: string): boolean {
      return !!(value && value.trim() && value.trim() !== '');
    }

    function listAreasAtuacao() {
      PedidoService.listAreasAtuacao(props.formSubstituicao.pedido.idTransportadora).subscribe({
        next: (response: any) => {
          areaAtuacao.value = response;
        },
        error: () => {
          console.error('Erro ao listar áreas de atuação.');
        },
      });
    }

    return {
      cpf,
      cooperado,
      erro,
      loading,
      rules,
      buscarCooperado,
      cep,
      endereco,
      enderecoEncontrado,
      erroCep,
      loadingCep,
      cepRules,
      buscarEndereco,
      formatDataNascimento,
      // onNumeroChange,
      sendForm,
      removeMascara,
      refForm,
      areaAtuacao,
      loadingSaveCooperado,
      numeroRules,
      isFieldFilled,
      camposPreenchidosAPI,
      veiculoSeraExcluido,
    };
  },
});
